// src/services/matchService.js
import api from '../api';

// ── Module-level stale-while-revalidate cache for getMatch ──
// Rapid navigation between pipeline ↔ detail ↔ back should be instant.
// Fresh cache (< FRESH_TTL_MS) returns immediately with no network.
// Stale cache returns immediately AND kicks off a background revalidation.
// In-flight requests are deduped so concurrent callers share one request.
const FRESH_TTL_MS = 60 * 1000; // data considered fresh for 60s
const STALE_TTL_MS = 5 * 60 * 1000; // still usable (with revalidation) for 5min
const matchCache = new Map(); // matchId → { data, fetchedAt }
const inflightMatchRequests = new Map(); // matchId → Promise

const fetchMatchFromServer = async (matchId) => {
  const existing = inflightMatchRequests.get(matchId);
  if (existing) return existing;
  const promise = api
    .get(`/matching/match/${matchId}`)
    .then((response) => {
      const data = response.data;
      matchCache.set(matchId, { data, fetchedAt: Date.now() });
      return data;
    })
    .finally(() => {
      inflightMatchRequests.delete(matchId);
    });
  inflightMatchRequests.set(matchId, promise);
  return promise;
};

/**
 * Invalidate a cached match (call after a mutation that changes its payload).
 * Also busts the axios-level cache (api.js DEDUPE_RULES) for the same
 * endpoint family so a subsequent GET re-fetches from server.
 */
export const invalidateMatchCache = (matchId) => {
  if (matchId) {
    matchCache.delete(matchId);
    if (typeof api.invalidateCache === 'function') {
      api.invalidateCache(`/matching/match/${matchId}`);
      api.invalidateCache(`/negotiations/state/${matchId}/available-transitions`);
    }
  } else {
    matchCache.clear();
    if (typeof api.invalidateCache === 'function') {
      api.invalidateCache('/matching/match/');
      api.invalidateCache('/negotiations/state/');
    }
  }
};

/**
 * Service for handling match-related API requests
 */
const matchService = {
  /**
   * Get a specific match by ID
   * @param {string} matchId - The ID of the match to retrieve
   * @param {object} [opts]
   * @param {boolean} [opts.force] - Bypass cache and refetch from server
   * @returns {Promise} - The match data
   */
  getMatch: async (matchId, opts = {}) => {
    if (!matchId) throw new Error('matchId required');
    try {
      const cached = matchCache.get(matchId);
      const age = cached ? Date.now() - cached.fetchedAt : Infinity;

      if (!opts.force && cached && age < FRESH_TTL_MS) {
        // Fresh — return cached, no network.
        return cached.data;
      }

      if (!opts.force && cached && age < STALE_TTL_MS) {
        // Stale but usable — kick off revalidation, return cached now.
        fetchMatchFromServer(matchId).catch((err) => {
          console.error('[matchService] background revalidation failed:', err);
        });
        return cached.data;
      }

      return await fetchMatchFromServer(matchId);
    } catch (error) {
      console.error('Error fetching match:', error);
      throw error;
    }
  },

  /**
   * Dismiss a match (decline the match)
   * @param {string} matchId - The ID of the match to dismiss
   * @param {Object} data - Optional additional data for dismissal (reason, feedback, etc.)
   * @returns {Promise} - The updated match
   */
  dismissMatch: async (matchId, data = {}) => {
    try {
      // Using the correct endpoint and method (PATCH) with the required status payload
      const response = await api.patch(`/matching/match-status/${matchId}`, {
        status: 'declined',
        ...data // Include any additional data provided
      });
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error('Error dismissing match:', error);
      throw error;
    }
  },

  /**
   * Get all matches for the current user
   * @returns {Promise} - Array of matches
   */
  getMatches: async () => {
    try {
      const response = await api.get('/matches');
      return response.data;
    } catch (error) {
      console.error('Error fetching matches:', error);
      throw error;
    }
  },

  /**
   * Get matches in negotiation status for the current user
   * @returns {Promise} - Array of matches in negotiation
   */
  getNegotiatingMatches: async () => {
    try {
      const response = await api.get('/matches?status=negotiating');
      return response.data;
    } catch (error) {
      console.error('Error fetching negotiating matches:', error);
      throw error;
    }
  },

  // finalizeMatch removed per PRODUCT-ETHOS.md I1 (audit #3): the backend
  // PUT /matches/:matchId/finalize endpoint was retired (it raw-wrote any status
  // with no guards). This method had no caller. Terminal outcomes go through the
  // state-machine decline/close paths and the disposition conclude endpoint.

  /**
   * Update the status of a match
   * @param {string} matchId - The ID of the match to update
   * @param {string} status - The new status ('declined', 'inNegotiation', 'hired', etc.)
   * @param {Object} data - Optional additional data for the status update
   * @returns {Promise} - The updated match
   */
  updateMatchStatus: async (matchId, status, data = {}) => {
    try {
      const response = await api.patch(`/matching/match-status/${matchId}`, { status, ...data });
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error(`Error updating match status to ${status}:`, error);
      throw error;
    }
  },

  /**
   * Conclude a negotiation (mark as complete)
   * Uses the new disposition system - does NOT track placement outcomes
   * @param {string} matchId - The ID of the match/negotiation to conclude
   * @param {string} note - Optional note about the conclusion
   * @returns {Promise} - The disposition update result
   */
  concludeNegotiation: async (matchId, note = '', conclusionReason = '') => {
    try {
      const response = await api.patch(`/matching/disposition/${matchId}/conclude`, { note, conclusionReason });
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error('Error concluding negotiation:', error);
      throw error;
    }
  },

  // =========================================================================
  // NEW NEGOTIATION STATE MACHINE ENDPOINTS
  // =========================================================================

  /**
   * Request an interview
   * @param {string} matchId - Swipe ID
   * @param {string} note - Optional note
   */
  requestInterviewState: async (matchId, note = '') => {
    try {
      const response = await api.post(`/negotiations/state/${matchId}/request-interview`, { note });
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error('Error requesting interview state:', error);
      throw error;
    }
  },

  /**
   * Atomically share contact details and request interview.
   * Replaces the two-step pattern of requestInterviewState + updateContactSharing.
   * @param {string} matchId - Swipe ID
   */
  shareContactAndAdvance: async (matchId) => {
    try {
      const response = await api.post(`/negotiations/state/${matchId}/share-contact-and-advance`);
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error('Error sharing contact and advancing:', error);
      throw error;
    }
  },

  /**
   * Generic state machine transition
   * @param {string} matchId - Swipe ID
   * @param {string} targetState - Target state
   * @param {string} note - Optional manual note
   */
  transitionState: async (matchId, targetState, note = '') => {
    try {
      const payload = { targetState };
      if (note) payload.note = note;
      const response = await api.post(`/negotiations/state/${matchId}/transition`, payload);
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error(`Error transitioning state to ${targetState}:`, error);
      throw error;
    }
  },

  /**
   * Atomically apply the free connection marker and advance state. The legacy
   * endpoint name is retained for API compatibility.
   * @param {string} matchId - Swipe ID
   * @param {Object} options - { note?: string, advanceToInterview?: boolean }
   */
  unlockAndAdvance: async (matchId, options = {}) => {
    try {
      const response = await api.post(
        `/negotiations/state/${matchId}/unlock-and-advance`,
        options,
      );
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error('Error unlocking and advancing negotiation:', error);
      throw error;
    }
  },

  /**
   * Update contact sharing preference for a match
   * @param {string} matchId - Swipe ID
   * @param {boolean} shareContact - Whether to share contact details
   */
  updateContactSharing: async (matchId, shareContact) => {
    try {
      const response = await api.patch(`/matching/share-contact/${matchId}`, { shareContact });
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error('Error updating contact sharing:', error);
      throw error;
    }
  },

  /**
   * Accept an interview request (provider only)
   * Sets the interviewAccepted flag without changing pipeline state
   * @param {string} matchId - Swipe ID
   */
  acceptInterviewState: async (matchId) => {
    try {
      const response = await api.post(`/negotiations/state/${matchId}/accept-interview`);
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error('Error accepting interview:', error);
      throw error;
    }
  },

  /**
   * Provider confirms the credentialing process
   * @param {string} matchId - Swipe ID
   */
  confirmCredentialing: async (matchId) => {
    try {
      const response = await api.post(`/negotiations/state/${matchId}/confirm-credentialing`);
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error('Error confirming credentialing:', error);
      throw error;
    }
  },

  /**
   * Decline a negotiation
   * @param {string} matchId - Swipe ID
   * @param {string} reason - Required decline reason
   */
  declineNegotiationState: async (matchId, reason) => {
    try {
      const response = await api.post(`/negotiations/state/${matchId}/decline`, { reason });
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error('Error declining negotiation state:', error);
      throw error;
    }
  },

  /**
   * Close a negotiation (different from conclude disposition)
   * @param {string} matchId - Swipe ID
   * @param {string} note - Optional note
   */
  closeNegotiationState: async (matchId, note = '') => {
    try {
      const response = await api.post(`/negotiations/state/${matchId}/close`, { note });
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error('Error closing negotiation state:', error);
      throw error;
    }
  },

  /**
   * Start credentialing (company only)
   * @param {string} matchId - Swipe ID
   * @param {string} credentialingProcessId - Optional credentialing process ref
   */
  startCredentialingState: async (matchId, credentialingProcessId) => {
    try {
      const response = await api.post(`/negotiations/state/${matchId}/start-credentialing`, { credentialingProcessId });
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error('Error starting credentialing state:', error);
      throw error;
    }
  },

  /**
   * Approve credentialing → hired (company only)
   * @param {string} matchId - Swipe ID
   */
  approveCredentialingState: async (matchId) => {
    try {
      const response = await api.post(`/negotiations/state/${matchId}/approve`);
      invalidateMatchCache(matchId);
      return response.data;
    } catch (error) {
      console.error('Error approving credentialing state:', error);
      throw error;
    }
  },

  /**
   * Get available transitions for a negotiation
   * @param {string} matchId - Swipe ID
   * @returns {Promise<{currentState: string, transitions: Array<{targetState: string, label: string}>}>}
   */
  getAvailableTransitions: async (matchId) => {
    try {
      const response = await api.get(`/negotiations/state/${matchId}/available-transitions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching available transitions:', error);
      throw error;
    }
  }
};

export default matchService;
